import { useState } from 'react';
import { ChevronDownIcon, ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEscapeKey } from '@odo/shared-ui';

// Right-side detail drawer chrome, ported locally from files-viewer's
// billing/Drawer.tsx (a files-viewer-local component, so replicated rather
// than imported across apps) so the doctor-facing invoice drawer shares the
// exact same design language as the admin ChargeDrawer: same panel width
// (max-w-[560px]), same 20%-opacity backdrop, same header layout (optional
// back chevron, title + badge, subtitle line, close button), and the same
// DrawerSection/KVRow section chrome.

interface DrawerProps {
    title: React.ReactNode;
    badge?: React.ReactNode;
    subtitle?: React.ReactNode;
    onClose: () => void;
    // Renders a back chevron before the title, for drawer-to-drawer
    // navigation. Unused today (the doctor drawer has no nested drawer to
    // navigate back from) but kept for parity with the ported pattern.
    onBack?: () => void;
    // Pinned action bar below the scrollable body (e.g. the Pay CTA) — a
    // deliberate addition beyond ChargeDrawer's own Drawer, which has no
    // footer slot (its action buttons live inside the scrollable body
    // instead). Rendered as a proper `shrink-0` flex sibling so it reserves
    // space rather than floating over the last section.
    footer?: React.ReactNode;
    // Blocks Escape and the backdrop click while something is layered above
    // the drawer (e.g. the export modal), so one dismiss only closes the top layer.
    closeDisabled?: boolean;
    children: React.ReactNode;
}

export function Drawer({
    title,
    badge,
    subtitle,
    onClose,
    onBack,
    footer,
    closeDisabled = false,
    children
}: DrawerProps) {
    useEscapeKey(onClose, !closeDisabled);

    return (
        <>
            <div
                className="fixed inset-0 bg-black bg-opacity-20 z-40"
                onClick={closeDisabled ? undefined : onClose}
            />
            <div className="fixed right-0 top-0 h-full w-full max-w-[560px] bg-white border-l border-gray-200 shadow-xl z-50 flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3 shrink-0 bg-white">
                    <div className="flex items-start gap-2 min-w-0">
                        {onBack && (
                            <button
                                type="button"
                                onClick={onBack}
                                aria-label="Back"
                                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 shrink-0 transition-colors -ml-2"
                            >
                                <ChevronLeftIcon className="h-5 w-5" />
                            </button>
                        )}
                        <div className="min-w-0">
                            <div className="flex items-center gap-2.5">
                                <h2 className="text-xl font-bold text-gray-800 truncate">
                                    {title}
                                </h2>
                                {badge}
                            </div>
                            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={closeDisabled}
                        aria-label="Close"
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-6 py-5 flex flex-col gap-6">
                    {children}
                </div>
                {footer && (
                    <div className="shrink-0 border-t border-gray-200 px-6 py-4 bg-white">
                        {footer}
                    </div>
                )}
            </div>
        </>
    );
}

interface DrawerSectionProps {
    title: string;
    action?: React.ReactNode;
    // Collapsible sections render their children only while open. Not used
    // by the doctor drawer's own sections today (none of them warrant
    // hiding by default), but ported for parity with the source pattern.
    collapsible?: boolean;
    defaultOpen?: boolean;
    children: React.ReactNode;
}

export function DrawerSection({
    title,
    action,
    collapsible = false,
    defaultOpen = true,
    children
}: DrawerSectionProps) {
    const [open, setOpen] = useState(defaultOpen);
    const isOpen = !collapsible || open;
    return (
        <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                {collapsible ? (
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="flex items-center gap-1.5 group"
                    >
                        <h3 className="text-[12px] font-bold tracking-[0.005em] text-neutral-500 uppercase group-hover:text-neutral-700 transition-colors">
                            {title}
                        </h3>
                        <ChevronDownIcon
                            className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                        />
                    </button>
                ) : (
                    <h3 className="text-[12px] font-bold tracking-[0.005em] text-neutral-500 uppercase">
                        {title}
                    </h3>
                )}
                {action}
            </div>
            {isOpen && children}
        </section>
    );
}

export function KVRow({ k, children }: { k: string; children: React.ReactNode }) {
    return (
        <div className="flex items-start justify-between gap-4 py-1">
            <span className="text-sm text-gray-500 shrink-0">{k}</span>
            <span className="text-sm text-gray-800 text-right">{children}</span>
        </div>
    );
}

interface AmountRowProps {
    label: string;
    sub?: string;
    value: string;
    minus?: boolean;
    strong?: boolean;
}

// Amount-breakdown row, ported from ChargeDrawer's AmountRow: a muted label
// (+ small sub-label) on the left, a tabular-nums value on the right — bold
// with a top divider for the "strong" totals (Charge total / Invoice total),
// red for "minus" deductions (discount, credits).
export function AmountRow({ label, sub, value, minus, strong }: AmountRowProps) {
    return (
        <div
            className={`flex items-center justify-between gap-3 ${strong ? 'pt-2.5 mt-1 border-t border-gray-200' : 'py-0.5'}`}
        >
            <span
                className={`text-sm min-w-0 truncate ${strong ? 'font-bold text-gray-900' : 'text-gray-500'}`}
                title={sub ? `${label} · ${sub}` : label}
            >
                {label}
                {sub && <span className="text-xs text-gray-400 font-normal"> · {sub}</span>}
            </span>
            <span
                className={`tabular-nums shrink-0 ${strong ? 'text-lg font-bold text-gray-900' : `text-sm ${minus ? 'text-red-600' : 'text-gray-800'}`}`}
            >
                {value}
            </span>
        </div>
    );
}
